import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';

export interface UsernameCheckResult {
  available: boolean;
  message: string;
  pathAvailable?: boolean;
  subdomainAvailable?: boolean;
}

export interface ExistingUsernameResult {
  username: string | null;
  hasPathUrl: boolean;
  hasSubdomain: boolean;
  source: 'course' | 'salepage' | null;
}

export async function checkUsernameAvailability(
  username: string,
  currentUserId?: string
): Promise<UsernameCheckResult> {
  const normalizedUsername = username.toLowerCase().trim();

  try {
    // Try to call Cloud Function for comprehensive validation
    const checkUsername = httpsCallable(functions, 'checkUsernameAvailability');
    const result = await checkUsername({ 
      username: normalizedUsername,
      type: 'both'
    });

    const data = result.data as UsernameCheckResult;
    return {
      available: data.available,
      message: data.message || (data.available ? 'Username นี้ใช้งานได้!' : 'Username นี้ถูกใช้งานแล้ว'),
      pathAvailable: data.pathAvailable,
      subdomainAvailable: data.subdomainAvailable
    };

  } catch (error: any) {
    console.warn('Cloud Function failed, using Firestore fallback:', error);
    
    // Fallback: Check directly in Firestore
    try {
      let pathAvailable = true;
      let subdomainAvailable = true;

      // Check in instructors collection for subdomain
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('subdomain', '==', normalizedUsername),
        limit(1)
      );
      const instructorsSnapshot = await getDocs(instructorsQuery);
      
      if (!instructorsSnapshot.empty) {
        const doc = instructorsSnapshot.docs[0];
        if (doc.id !== currentUserId) {
          subdomainAvailable = false;
        }
      }

      // Check in urlAddons collection
      const urlAddonsQuery = query(
        collection(db, 'urlAddons'),
        where('username', '==', normalizedUsername),
        limit(1)
      );
      const urlAddonsSnapshot = await getDocs(urlAddonsQuery);
      
      if (!urlAddonsSnapshot.empty) {
        const doc = urlAddonsSnapshot.docs[0];
        const data = doc.data();
        
        if (doc.id !== currentUserId) {
          if (data.hasPathUrl) pathAvailable = false;
          if (data.hasSubdomain) subdomainAvailable = false;
        }
      }

      // Check in subscriptions collection
      const subscriptionsQuery = query(
        collection(db, 'subscriptions'),
        where('username', '==', normalizedUsername),
        limit(1)
      );
      const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
      
      if (!subscriptionsSnapshot.empty) {
        const doc = subscriptionsSnapshot.docs[0];
        const data = doc.data();
        
        if (data.userId !== currentUserId) {
          if (data.hasPathUrl) pathAvailable = false;
          if (data.hasSubdomain) subdomainAvailable = false;
        }
      }

      // Check in salePageSubscriptions collection
      const salePageQuery = query(
        collection(db, 'salePageSubscriptions'),
        where('username', '==', normalizedUsername),
        limit(1)
      );
      const salePageSnapshot = await getDocs(salePageQuery);
      
      if (!salePageSnapshot.empty) {
        const doc = salePageSnapshot.docs[0];
        const data = doc.data();
        
        if (doc.id !== currentUserId) {
          if (data.hasPathUrl) pathAvailable = false;
          if (data.hasSubdomain) subdomainAvailable = false;
        }
      }

      const available = pathAvailable && subdomainAvailable;

      if (!available) {
        let message = 'Username นี้ถูกใช้งานแล้ว';
        if (!pathAvailable && !subdomainAvailable) {
          message = 'Username นี้ถูกใช้งานแล้วทั้ง Path URL และ Subdomain';
        } else if (!pathAvailable) {
          message = 'Username นี้ถูกใช้งานแล้วสำหรับ Path URL';
        } else if (!subdomainAvailable) {
          message = 'Username นี้ถูกใช้งานแล้วสำหรับ Subdomain';
        }

        return {
          available: false,
          message,
          pathAvailable,
          subdomainAvailable
        };
      }

      return {
        available: true,
        message: 'Username นี้ใช้งานได้!',
        pathAvailable: true,
        subdomainAvailable: true
      };

    } catch (fallbackError) {
      console.error('Firestore fallback also failed:', fallbackError);
      return {
        available: false,
        message: 'ไม่สามารถตรวจสอบ Username ได้ กรุณาลองอีกครั้ง'
      };
    }
  }
}

/**
 * Get existing username from other system (SalePage or Course)
 * @param userId - Current user ID
 * @param targetSystem - System we're checking for ('course' or 'salepage')
 * @returns Existing username info from the other system
 */
export async function getUsernameFromOtherSystem(
  userId: string,
  targetSystem: 'course' | 'salepage'
): Promise<ExistingUsernameResult> {
  try {
    // If target is course, check SalePage
    // If target is salepage, check Course subscriptions
    const sourceSystem = targetSystem === 'course' ? 'salepage' : 'course';
    
    if (sourceSystem === 'salepage') {
      // Check salePageSubscriptions
      const salePageQuery = query(
        collection(db, 'salePageSubscriptions'),
        where('__name__', '==', userId),
        limit(1)
      );
      const salePageSnapshot = await getDocs(salePageQuery);
      
      if (!salePageSnapshot.empty) {
        const data = salePageSnapshot.docs[0].data();
        if (data.username) {
          return {
            username: data.username,
            hasPathUrl: data.hasPathUrl || false,
            hasSubdomain: data.hasSubdomain || false,
            source: 'salepage'
          };
        }
      }
    } else {
      // Check subscriptions (Course system)
      const subscriptionsQuery = query(
        collection(db, 'subscriptions'),
        where('userId', '==', userId),
        limit(1)
      );
      const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
      
      if (!subscriptionsSnapshot.empty) {
        const data = subscriptionsSnapshot.docs[0].data();
        if (data.username) {
          return {
            username: data.username,
            hasPathUrl: data.hasPathUrl || false,
            hasSubdomain: data.hasSubdomain || false,
            source: 'course'
          };
        }
      }
    }
    
    return {
      username: null,
      hasPathUrl: false,
      hasSubdomain: false,
      source: null
    };
  } catch (error) {
    console.error('Error getting username from other system:', error);
    return {
      username: null,
      hasPathUrl: false,
      hasSubdomain: false,
      source: null
    };
  }
}
